import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

// Re-authored from ../design-system/components/forms/RadioGroup.jsx.
//
// Structure per option:
//   <label>
//     <input type="radio" class="peer sr-only">   ← the peer
//     <span class="ring-dot …">                   ← peer sibling: peer-* variants work here
//       <span class="inner-dot …">               ← child of ring; driven via [&>span] on the ring
//     </span>
//     <span class="label text">…</span>
//   </label>
//
// The DS uses a CSS ::after pseudo-element for the inner dot. We re-author it as
// a real <span> child so it's utility-able and DOM-testable. The reveal is
// placed on the RING span (`peer-checked:[&>span]:scale-100`) because
// peer-* only targets general siblings of the peer input; a descendant of a sibling
// must be reached via an arbitrary child selector on that sibling — identical to how
// checkbox uses `peer-checked:[&_svg]:opacity-100` on the box span.
//
// name defaults to a stable useId() — NOT Math.random() (the DS used random; avoid
// for SSR/test stability). Controlled when `value !== undefined`.

export type RadioOption = string | { value: string; label: ReactNode };

export interface RadioGroupProps {
  name?: string;
  options: RadioOption[];
  value?: string;           // controlled
  defaultValue?: string;    // uncontrolled
  onChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void;
  row?: boolean;
  disabled?: boolean;
  className?: string;
}

export function RadioGroup({
  name,
  options,
  value,
  defaultValue,
  onChange,
  row = false,
  disabled = false,
  className,
}: RadioGroupProps) {
  const autoId = useId();
  const groupName = name ?? autoId;
  const isControlled = value !== undefined;

  return (
    <div
      role="radiogroup"
      className={cn(
        'flex font-sans',
        row ? 'flex-row flex-wrap gap-6' : 'flex-col gap-3',
        className,
      )}
    >
      {options.map((o) => {
        const opt = typeof o === 'string' ? { value: o, label: o } : o;
        const checked = isControlled ? value === opt.value : undefined;
        const defaultChecked = !isControlled ? defaultValue === opt.value : undefined;

        return (
          <label
            key={opt.value}
            className={cn(
              'inline-flex items-start gap-3 cursor-pointer select-none',
              'has-[:disabled]:opacity-50 has-[:disabled]:cursor-not-allowed',
            )}
          >
            {/* Visually-hidden native radio — the "peer" */}
            <input
              type="radio"
              name={groupName}
              value={opt.value}
              checked={checked}
              defaultChecked={defaultChecked}
              disabled={disabled}
              onChange={
                onChange
                  ? (e: React.ChangeEvent<HTMLInputElement>) =>
                      onChange(opt.value, e)
                  : undefined
              }
              className="peer sr-only"
            />

            {/*
             * Ring dot — peer sibling, so peer-* variants work here.
             * Inner dot is a real <span> child; driven via [&>span]:scale-*
             * on this ring span (the peer-* selector must live on the sibling,
             * and the arbitrary child combinator reaches the inner span).
             */}
            <span
              className={cn(
                // Base ring
                'flex-none h-5 w-5 mt-px flex items-center justify-center',
                'rounded-full border-[1.5px] border-border bg-card',
                'transition-[border-color,box-shadow]',
                // Checked: ring turns navy
                'peer-checked:border-primary',
                // Focus-visible: gold glow ring
                'peer-focus-visible:ring-[3px] peer-focus-visible:ring-[var(--focus-glow)]',
                // Inner dot: scale from 0 to 1 when checked (driven here, not on the child)
                '[&>span]:scale-0 peer-checked:[&>span]:scale-100',
              )}
            >
              {/* Inner dot — the revealed fill; its scale is driven by the parent via [&>span] */}
              <span
                className={cn(
                  'h-2.5 w-2.5 rounded-full bg-primary',
                  'transition-transform duration-150',
                )}
              />
            </span>

            {/* Label text */}
            <span className="text-base text-foreground leading-snug">
              {opt.label}
            </span>
          </label>
        );
      })}
    </div>
  );
}
